import { useMutation } from "@apollo/client";

import FlashContext from "components/FlashMessages/FlashContext";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import TableSimpleCell from "ds/components/TableSimple/Cell";
import { TableSimpleCellProps } from "ds/components/TableSimple/types";
import useTypedContext from "hooks/useTypedContext";
import useCanCreateScan from "shared/Scan/access/useCanCreateScan";
import useCanDeleteScan from "shared/Scan/access/useCanDeleteScan";
import useCanTriggerScan from "shared/Scan/access/useCanTriggerScan";
import { SCAN_RUN_TRIGGER } from "views/Account/Scans/ScanDetail/gql";

import { showDeleteConfirmation } from "../../DeleteConfirmation";
import { ScansListItem } from "../../../types";

const ScansTableDropdownCell = ({
  item: scan,
  ...cellProps
}: TableSimpleCellProps<ScansListItem>) => {
  const { onError, reportSuccess } = useTypedContext(FlashContext);
  const canCreateScan = useCanCreateScan();
  const canDeleteScan = useCanDeleteScan()(scan);
  const canTriggerScan = useCanTriggerScan()(scan);

  const [triggerScanRun, { loading: isTriggering }] = useMutation(SCAN_RUN_TRIGGER, {
    refetchQueries: ["SearchScans", "SearchScansCount"],
    awaitRefetchQueries: true,
  });

  const handleTrigger = () => {
    triggerScanRun({ variables: { scan: scan.id } })
      .then(({ data }) => {
        if (data?.scanRunTrigger.id) {
          reportSuccess({ message: "Scan successfully triggered" });
        }
      })
      .catch(onError);
  };

  const handleDelete = () => {
    showDeleteConfirmation({ id: scan.id, name: scan.name });
  };

  return (
    <TableSimpleCell {...cellProps}>
      <DropdownMenuEllipsis tooltip="Scan actions">
        {(canTriggerScan.result || canTriggerScan.message) && (
          <DropdownMenuItem
            onAction={handleTrigger}
            isDisabled={!canTriggerScan.result || isTriggering}
            loading={isTriggering}
            tooltip={canTriggerScan.message}
            tooltipPlacement="left"
          >
            Trigger scan
          </DropdownMenuItem>
        )}
        {(canCreateScan.result || canCreateScan.message) && (
          <DropdownMenuItem
            href={`/resources/scans/${scan.id}/edit`}
            isDisabled={!canCreateScan.result}
            tooltip={canCreateScan.message}
            tooltipPlacement="left"
          >
            Edit scan
          </DropdownMenuItem>
        )}
        {(canDeleteScan.result || canDeleteScan.message) && (
          <DropdownMenuItem
            onAction={handleDelete}
            isDisabled={!canDeleteScan.result}
            tooltip={canDeleteScan.message}
            tooltipPlacement="left"
            danger
          >
            Delete scan
          </DropdownMenuItem>
        )}
      </DropdownMenuEllipsis>
    </TableSimpleCell>
  );
};

export default ScansTableDropdownCell;
